import type { WhatsWorkingContext } from '../models/whatsWorking.types'

/** Shared JSON-decoding primitives for the `whats_working` and `combinations` boundaries — both
 * informal DTOs (contract 23 §BE-23-15) that carry the same `{value, display}` context shape. */

export function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

export function valueOrEmpty(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

/** Defensive: the row shape isn't a locked DTO yet, so a missing/malformed `display` degrades to
 * "no context" rather than a partially-decoded chip. */
export function decodeContext(value: unknown): WhatsWorkingContext | null {
  const source = record(value)
  if (!source || typeof source.display !== 'string' || source.display.length === 0) return null
  return { value: valueOrEmpty(source.value), display: source.display }
}

export function decodeCreativeIds(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return []
  return value.filter((id) => typeof id === 'string' || typeof id === 'number').map(String)
}

/**
 * Legacy parity: every `*_diff_percent`/`*_improvement_pct` field on this boundary (old's
 * `formatFixed(value) + '%'`, never a ratio multiplier) arrives as an already-scaled percentage
 * (`300.34` meaning "+300.34%"), not a 0–1 ratio. This app's own formatting (`formatPercent`'s
 * `scale: 'ratio'`) and tone intensity (`deltaTone`'s magnitude thresholds) both assume a ratio —
 * normalizing here, once, at the boundary keeps every downstream consumer correct without each
 * one needing to know which convention a given field arrived in.
 */
export function percentToRatio(value: number | null): number | null {
  return value === null ? null : value / 100
}
